import { Component, inject } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AppButtonComponent } from '../button';

export interface ConfirmDialogData {
  title: string;
  message?: string;
  confirmLabel?: string;
  /** null = no cancel button (alert mode) */
  cancelLabel?: string | null;
  icon?: 'warning' | 'error' | 'success' | 'info';
}

@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  imports: [AppButtonComponent],
  template: `
    <div class="p-6 min-w-[320px] max-w-[480px]">
      <div class="flex flex-col items-center text-center mb-5">
        @if (data.icon === 'warning') {
          <div class="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mb-3">
            <svg class="h-7 w-7 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
        }
        @if (data.icon === 'error') {
          <div class="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-3">
            <svg class="h-7 w-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        }
        @if (data.icon === 'success') {
          <div class="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-3">
            <svg class="h-7 w-7 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        }
        @if (data.icon === 'info') {
          <div class="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mb-3">
            <svg class="h-7 w-7 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        }
        <h2 class="text-base font-semibold text-slate-900 leading-snug">{{ data.title }}</h2>
        @if (data.message) {
          <p class="text-sm text-slate-500 mt-1.5">{{ data.message }}</p>
        }
      </div>
      <div class="flex gap-3 justify-center">
        @if (data.cancelLabel !== null) {
          <app-button
            type="button"
            variant="secondary"
            size="sm"
            testId="dialog-cancel"
            (pressed)="dialogRef.close(false)">
            {{ data.cancelLabel ?? 'Annuler' }}
          </app-button>
        }
        <app-button type="button" variant="primary" size="sm" testId="dialog-confirm" (pressed)="dialogRef.close(true)">
          {{ data.confirmLabel ?? 'Confirmer' }}
        </app-button>
      </div>
    </div>
  `,
})
export class ConfirmDialogComponent {
  readonly data = inject<DynamicDialogConfig<ConfirmDialogData>>(DynamicDialogConfig).data!;
  readonly dialogRef = inject(DynamicDialogRef);
}
